import React, { FunctionComponent, useMemo, useState } from 'react';

import classnames from 'classnames';
import { keys } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Route } from 'core/util/Route';
import { useReview } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, Label } from 'reviews2/feed/review-yeti/Review.types';
import { RespondableType } from 'reviews2/feed/Reviews.types';

import 'reviews2/feed/review-yeti/labels/ReviewLabels.scss';

interface LabelProps {
    name: string;
    label: string;
    icon: string;
    collapse: boolean;
    count: number;
    url?: string;
}

const MAX_FULL_BANNER = 4;

const ReviewLabel: FunctionComponent<LabelProps> = ({ name, label, icon, collapse, count, url }) => {
    const iconOnly = collapse && count >= MAX_FULL_BANNER;

    if (url) {
        return (
            <div
                className={classnames(`r4e-review-label ${name}`, {
                    'label-icon': iconOnly,
                })}
            >
                <i className={icon} />
                {!iconOnly && (
                    <span className="r4e-review-label-text">
                        <a
                            href={url}
                            className="d-flex flex-column"
                            rel="noopener noreferrer"
                            tabIndex={0}
                            aria-label={label}
                            title={label}
                        >
                            {label}
                        </a>
                    </span>
                )}
            </div>
        );
    }

    return (
        <div
            className={classnames(`r4e-review-label ${name}`, {
                'label-icon': iconOnly,
            })}
        >
            <OverlayTrigger placement="top" overlay={<Tooltip id={name}>{label}</Tooltip>}>
                <i className={icon} />
            </OverlayTrigger>
            {!iconOnly && <span className="r4e-review-label-text">{label}</span>}
        </div>
    );
};

export const ReviewLabels: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const [{ review, isNorthStarEnabled }] = useReview();
    const [count, setCount] = useState(0);
    const { collapse } = context.options?.labelOptions!;
    const hideCreateTicketLabel = context.options?.actionOptions?.hideCreateTicket;
    const labels = useMemo(() => {
        const flags: Record<string, boolean> = {};

        if (review.respondable_type === RespondableType.Responded) {
            flags.responded = true;
        }

        if (context.reviewTicket) {
            flags.ticketCreated = true;
        }

        review?.r4e_label_ids?.forEach((label: number) => {
            switch (label) {
                case Label.Read:
                    flags.read = true;
                    break;
                case Label.Flagged:
                    flags.flagged = true;
                    break;
                case Label.Published:
                    flags.published = true;
                    break;
                case Label.Profanity:
                    flags.profanity = true;
                    break;
                case Label.Syndicated:
                    flags.syndicated = true;
                    break;
                case Label.Unsyndicated:
                    flags.unsyndicated = true;
                    break;
                case Label.Updated:
                    flags.updated = true;
                    break;
                case Label.NeedsResponse:
                    flags.needsResponse = true;
                    break;
                case Label.NoResponseRequired:
                    flags.noResponseRequired = true;
                    break;
                default:
                    break;
            }
        });

        setCount(keys(flags).length);

        return flags;
    }, [context.reviewTicket, review]);

    if (!labels) {
        return null;
    }

    return (
        <div
            className={classnames('r4e-review-labels', {
                'north-star': isNorthStarEnabled,
            })}
        >
            <div className="labels">
                {labels.flagged && (
                    <ReviewLabel
                        name="flagged"
                        label={t('FLAGGED')}
                        icon="icon-exclamation-circle"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.read && (
                    <ReviewLabel name="read" label={t('READ')} icon="icon-check" collapse={collapse!} count={count} />
                )}
                {labels.responded && (
                    <ReviewLabel
                        name="responded"
                        label={t('RESPONDED')}
                        icon="icon-reply"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.updated && (
                    <ReviewLabel
                        name="updated"
                        label={t('UPDATED')}
                        icon="icon-pencil"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.published && (
                    <ReviewLabel
                        name="published"
                        label={t('PUBLISHED')}
                        icon="icon-globe"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.requested && (
                    <ReviewLabel
                        name="requested"
                        label={t('REQUESTED')}
                        icon="icon-envelope"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.syndicated && (
                    <ReviewLabel
                        name="syndicated"
                        label={t('SYNDICATED')}
                        icon="icon-retweet"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.unsyndicated && (
                    <ReviewLabel
                        name="unsyndicated"
                        label={t('UNSYNDICATED')}
                        icon="icon-retweet"
                        collapse={collapse!}
                        count={count}
                    />
                )}

                {labels.profanity && (
                    <ReviewLabel
                        name="profanity"
                        label={t('PROFANITY')}
                        icon="icon-ban"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.responsePendingApproval && (
                    <ReviewLabel
                        name="pending-approval"
                        label={t('PENDING_APPROVAL')}
                        icon="icon-hourglass"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.needsResponse && (
                    <ReviewLabel
                        name="needs-response"
                        label={t('NEEDS_RESPONSE')}
                        icon="icon-exclamation-triangle"
                        collapse={collapse!}
                        count={count}
                    />
                )}
                {labels.ticketCreated && !hideCreateTicketLabel && (
                    <ReviewLabel
                        name="ticket-created"
                        label={t('VIEW_TICKET')}
                        icon="icon-ticket"
                        collapse={collapse!}
                        count={count}
                        url={context.reviewTicket ? `${Route.TenantActions}/tickets/${context.reviewTicket.id}` : ''}
                    />
                )}
                {labels.noResponseRequired && (
                    <ReviewLabel
                        name="no-response-required"
                        label={t('NO_RESPONSE_REQUIRED')}
                        icon="icon-exclamation-triangle"
                        collapse={collapse!}
                        count={count}
                    />
                )}
            </div>
        </div>
    );
};
